import React from "react";
import { Link } from "react-router-dom";

/**
 * Privacy Policy — placeholder/template content. Have it reviewed before
 * relying on it in regulated jurisdictions (CCPA, GDPR, PIPEDA, etc.).
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F4F2EC] text-black">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-[#C2410C]">← Vendor Vault</Link>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mt-6">Legal</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase mt-1">Privacy Policy</h1>
        <div className="font-mono text-xs text-[#4A4A4A] mt-2">Effective: February 2026</div>

        <div className="prose prose-sm max-w-none mt-8 font-mono text-sm leading-relaxed space-y-5">
          <p>Vendor Vault ("we", "us") respects your privacy. This Policy explains what we collect and why.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">1. What we collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account data</strong>: email, name, hashed password.</li>
            <li><strong>Business data you enter</strong>: products, variants, sales, events, photos.</li>
            <li><strong>Connected service tokens</strong>: encrypted OAuth tokens for Square and Shopify, used only to call those APIs on your behalf.</li>
            <li><strong>Billing data</strong>: handled entirely by Stripe — we only store your Stripe customer/subscription IDs, never card numbers.</li>
            <li><strong>Operational data</strong>: server logs, error reports, IP addresses for security purposes.</li>
          </ul>

          <h2 className="font-heading text-lg font-black uppercase mt-6">2. How we use it</h2>
          <p>To provide the Service, send transactional email (receipts, invites, password resets,
            verification), keep the platform secure, and improve performance. We do not sell your data.
            We do not use it to train AI models.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">3. Third-party processors</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe</strong> — payments &amp; subscriptions.</li>
            <li><strong>Square</strong> — when you connect, for in-person payments.</li>
            <li><strong>Shopify</strong> — when you connect, for catalog sync.</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>MongoDB Atlas</strong> &amp; <strong>Emergent</strong> — application hosting.</li>
          </ul>

          <h2 className="font-heading text-lg font-black uppercase mt-6">4. Your rights</h2>
          <p>You can export all your data from <code>/billing</code>, request deletion of your account
            (purges everything within 30 days), or contact us with any request at
            <a href="mailto:support@vendorvaultonline.com"> support@vendorvaultonline.com</a>.</p>
          <p>If you're in the EU/UK, you have GDPR rights of access, rectification, erasure, restriction,
            portability, and objection. If you're in California, you have CCPA rights of know, delete,
            and non-discrimination.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">5. Retention</h2>
          <p>We keep your data while your account is active. After deletion, we purge it within 30 days,
            except as required by law (e.g. tax records may need to be retained longer).</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">6. Security</h2>
          <p>Passwords are bcrypt-hashed. OAuth tokens are stored encrypted at rest in our database.
            We use TLS in transit. No system is 100% secure — please report any vulnerabilities to
            <a href="mailto:security@vendorvaultonline.com"> security@vendorvaultonline.com</a>.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">7. Children</h2>
          <p>Vendor Vault is not directed at children under 16. We do not knowingly collect data from them.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">8. Changes</h2>
          <p>If we change this Policy materially, we'll email you. Continued use means acceptance.</p>

          <h2 className="font-heading text-lg font-black uppercase mt-6">9. Contact</h2>
          <p>Questions? <a href="mailto:support@vendorvaultonline.com">support@vendorvaultonline.com</a></p>
        </div>

        <div className="mt-12 pt-6 border-t-2 border-black flex flex-wrap gap-4 font-mono text-xs">
          <Link to="/terms" className="underline">Terms of Service</Link>
          <Link to="/" className="underline">Home</Link>
        </div>
      </div>
    </div>
  );
}
